import { STORAGE_KEYS } from '../constants';
import { SaveManager } from '../systems/SaveManager';
import type { LevelStats } from '../systems/LevelStatsTracker';

// ============================================
// ProgressState (Step 12 Integration)
// ============================================

interface ScenarioProgress {
  unlocked: boolean;
  completed: boolean;
  attempts: number;
  bestScore: number;
  bestTime: number;
  bestPlayerCount?: number; // 1–4, for the run that achieved best score
}

interface ProgressData {
  scenarios: Record<string, ScenarioProgress>;
  levelStats: Record<string, LevelStats>;  // Stats for each completed level
  totalPlayTime: number;
  lastPlayed: string | null;
}

// All scenario IDs
const ALL_SCENARIO_IDS = [
  // Region 1 - Forest
  'F1', 'F2', 'F2C', 'F3', 'F4',
  // Region 2 - Swamp
  'S1', 'S2', 'S2C', 'S3', 'S4',
  // Region 3 - Ruins
  'R1', 'R2', 'R2C', 'R3', 'R4',
  // Region 4 - Volcano
  'V1', 'V2', 'V2C', 'V3', 'V4',
];

const DEFAULT_PROGRESS: ProgressData = {
  scenarios: {
    'F1': {
      unlocked: true,  // First scenario always unlocked
      completed: false,
      attempts: 0,
      bestScore: 0,
      bestTime: 0,
    },
  },
  levelStats: {},
  totalPlayTime: 0,
  lastPlayed: null,
};

class ProgressStateManager {
  private data: ProgressData;

  constructor() {
    this.data = this.load();
  }

  // ============================================
  // Persistence
  // ============================================

  private load(): ProgressData {
    try {
      // First try to load from SaveManager
      SaveManager.init();
      const slotData = SaveManager.getProgressData() as ProgressData;
      if (slotData && Object.keys(slotData).length > 0) {
        return {
          ...DEFAULT_PROGRESS,
          ...slotData,
          scenarios: {
            ...DEFAULT_PROGRESS.scenarios,
            ...(slotData.scenarios || {}),
          },
          levelStats: {
            ...DEFAULT_PROGRESS.levelStats,
            ...(slotData.levelStats || {}),
          },
        };
      }

      // Fallback to legacy localStorage
      const saved = localStorage.getItem(STORAGE_KEYS.PROGRESS);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...DEFAULT_PROGRESS,
          ...parsed,
          scenarios: {
            ...DEFAULT_PROGRESS.scenarios,
            ...(parsed.scenarios || {}),
          },
          levelStats: {
            ...DEFAULT_PROGRESS.levelStats,
            ...(parsed.levelStats || {}),
          },
        };
      }
    } catch (e) {
      console.error('[ProgressState] Failed to load:', e);
    }
    return { ...DEFAULT_PROGRESS };
  }

  private save(): void {
    try {
      // Save to legacy localStorage (for backwards compatibility)
      localStorage.setItem(STORAGE_KEYS.PROGRESS, JSON.stringify(this.data));
      
      // Also save to SaveManager
      const completedCount = this.getCompletedCount();
      const totalStars = this.getTotalStars();
      SaveManager.saveProgressData(this.data, completedCount, totalStars);
    } catch (e) {
      console.error('[ProgressState] Failed to save:', e);
    }
  }

  /**
   * Reload progress from current save slot
   */
  reload(): void {
    this.data = this.load();
    console.log('[ProgressState] Reloaded from save slot');
  }

  /**
   * Get count of completed scenarios
   */
  getCompletedCount(): number {
    return Object.values(this.data.scenarios).filter(s => s.completed).length;
  }

  /**
   * Get total stars earned (3 stars max per completed scenario)
   */
  getTotalStars(): number {
    // For now, count completed scenarios * 3 (simplified)
    // In a full implementation, you'd track stars per scenario
    return this.getCompletedCount() * 3;
  }

  // ============================================
  // Scenario Progress
  // ============================================

  getScenarioProgress(scenarioId: string): ScenarioProgress {
    if (!this.data.scenarios[scenarioId]) {
      // Create default entry (Tutorial and F1 always unlocked)
      this.data.scenarios[scenarioId] = {
        unlocked: scenarioId === 'F1' || scenarioId === 'Tutorial',
        completed: false,
        attempts: 0,
        bestScore: 0,
        bestTime: 0,
        bestPlayerCount: 1,
      };
    }
    return { ...this.data.scenarios[scenarioId] };
  }

  isScenarioUnlocked(scenarioId: string): boolean {
    return this.getScenarioProgress(scenarioId).unlocked;
  }

  isScenarioCompleted(scenarioId: string): boolean {
    return this.getScenarioProgress(scenarioId).completed;
  }

  /**
   * Unlock a scenario
   */
  unlockScenario(scenarioId: string): void {
    const progress = this.getScenarioProgress(scenarioId);
    if (!progress.unlocked) {
      progress.unlocked = true;
      this.data.scenarios[scenarioId] = progress;
      this.save();
      console.log(`[ProgressState] Unlocked: ${scenarioId}`);
    }
  }

  /**
   * Record an attempt at a scenario
   */
  recordAttempt(scenarioId: string): void {
    const progress = this.getScenarioProgress(scenarioId);
    progress.attempts++;
    this.data.scenarios[scenarioId] = progress;
    this.data.lastPlayed = scenarioId;
    this.save();
  }

  /**
   * Record a victory with score and time
   * Also unlocks scenarios specified in the scenario's unlocks array
   * @param playerCount 1–4, stored with best when this run sets a new best
   */
  recordVictory(scenarioId: string, score: number, timeElapsed: number, playerCount: number = 1, unlockScenarios?: string[]): void {
    const progress = this.getScenarioProgress(scenarioId);

    progress.completed = true;

    // Keep best score as the all-time high (never overwrite with a lower value)
    const previousBest = progress.bestScore ?? 0;
    const isNewBestScore = score > previousBest;
    progress.bestScore = Math.max(previousBest, score);

    // Update best time if faster (or if first completion)
    const isNewBestTime = progress.bestTime === 0 || timeElapsed < progress.bestTime;
    if (isNewBestTime) {
      progress.bestTime = timeElapsed;
    }
    if (isNewBestScore || isNewBestTime) {
      progress.bestPlayerCount = playerCount;
    }

    this.data.scenarios[scenarioId] = progress;

    // Unlock next scenarios
    if (unlockScenarios) {
      unlockScenarios.forEach(id => this.unlockScenario(id));
    }

    this.save();

    console.log(`[ProgressState] Victory on ${scenarioId}: Score=${score}, Time=${timeElapsed}`);
  }

  /**
   * Save level stats for a completed level
   */
  saveLevelStats(stats: LevelStats): void {
    if (!this.data.levelStats) {
      this.data.levelStats = {};
    }
    this.data.levelStats[stats.scenarioId] = stats;
    this.save();
    console.log(`[ProgressState] Saved level stats for ${stats.scenarioId}`);
  }

  /**
   * Get level stats for a specific scenario
   */
  getLevelStats(scenarioId: string): LevelStats | null {
    return this.data.levelStats?.[scenarioId] ?? null;
  }

  /**
   * Check if we have stats for a scenario
   */
  hasLevelStats(scenarioId: string): boolean {
    return !!this.data.levelStats?.[scenarioId];
  }

  /**
   * Unlock a tower (placeholder for tower progression tracking)
   */
  unlockTower(towerType: string): void {
    // Could track tower unlocks here if needed
    console.log(`[ProgressState] Tower unlocked: ${towerType}`);
  }

  /**
   * Get completed challenges (placeholder)
   */
  getCompletedChallenges(): string[] {
    // Return completed challenge scenarios
    return ALL_SCENARIO_IDS
      .filter(id => id.includes('C'))  // Challenge scenarios end with 'C'
      .filter(id => this.isScenarioCompleted(id));
  }

  /**
   * Get all scenario IDs that have progress
   */
  getAllScenarioIds(): string[] {
    return Object.keys(this.data.scenarios);
  }

  /**
   * Get all completed scenarios
   */
  getCompletedScenarios(): string[] {
    return Object.entries(this.data.scenarios)
      .filter(([_, progress]) => progress.completed)
      .map(([id, _]) => id);
  }

  /**
   * Get all unlocked scenarios
   */
  getUnlockedScenarios(): string[] {
    return Object.entries(this.data.scenarios)
      .filter(([_, progress]) => progress.unlocked)
      .map(([id, _]) => id);
  }

  // ============================================
  // Global Stats
  // ============================================

  getTotalPlayTime(): number {
    return this.data.totalPlayTime;
  }

  addPlayTime(seconds: number): void {
    this.data.totalPlayTime += seconds;
    this.save();
  }

  getLastPlayedScenario(): string | null {
    return this.data.lastPlayed;
  }

  getTotalAttempts(): number {
    return Object.values(this.data.scenarios).reduce((sum, p) => sum + p.attempts, 0);
  }

  getTotalVictories(): number {
    return Object.values(this.data.scenarios).filter(p => p.completed).length;
  }

  // ============================================
  // Reset
  // ============================================

  reset(): void {
    this.data = { ...DEFAULT_PROGRESS };
    this.save();
    console.log('[ProgressState] Reset to defaults');
  }

  /**
   * Reset a single scenario
   */
  resetScenario(scenarioId: string): void {
    if (this.data.scenarios[scenarioId]) {
      const wasUnlocked = this.data.scenarios[scenarioId].unlocked;
      this.data.scenarios[scenarioId] = {
        unlocked: wasUnlocked || scenarioId === 'F1',
        completed: false,
        attempts: 0,
        bestScore: 0,
        bestTime: 0,
        bestPlayerCount: 1,
      };
      this.save();
    }
  }

  // ============================================
  // Debug/Dev
  // ============================================

  /**
   * Unlock all scenarios (for debugging)
   */
  unlockAll(): void {
    // Unlock all known scenarios
    ALL_SCENARIO_IDS.forEach(id => {
      if (!this.data.scenarios[id]) {
        this.data.scenarios[id] = {
          unlocked: true,
          completed: false,
          attempts: 0,
          bestScore: 0,
          bestTime: 0,
          bestPlayerCount: 1,
        };
      } else {
        this.data.scenarios[id].unlocked = true;
      }
    });

    this.save();
    console.log('[ProgressState] All scenarios unlocked');
  }

  /**
   * Get raw data (for debugging)
   */
  getDebugData(): ProgressData {
    return { ...this.data };
  }
}

// Singleton instance
export const ProgressState = new ProgressStateManager();
